import type { CSSProperties } from "react";
import type { AgendaSettings } from "@bookdental/schemas";

export type CardStyle = AgendaSettings["cardStyle"];

export type CardStyleSpec = {
  /** Estilos inline aplicados ao botão do card. */
  style: CSSProperties;
  /** Contrast de texto — sobrescreve via className do componente. */
  textClassName?: string;
};

/** Converte hex (#RRGGBB) em rgb para usar em rgba() com alpha dinâmico. */
function hexToRgb(hex: string): [number, number, number] {
  const clean = hex.replace("#", "");
  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);
  return [r, g, b];
}

/** Determina se a cor é clara (retorna true) ou escura — luminância relativa. */
function isLight(hex: string): boolean {
  const [r, g, b] = hexToRgb(hex);
  // Fórmula de luminância perceptual (W3C)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6;
}

/**
 * Dado a cor base de um appointment (hex) + o cardStyle escolhido,
 * retorna a spec visual completa pro AppointmentCard.
 *
 * Retrocompatível: colorful/light/assorted usam a estética antiga
 * (bg claro + borda lateral sólida). Os 5 novos estilos dão variações
 * visuais profissionais adequadas a ambiente clínico.
 */
export function getCardStyleSpec(hex: string, cardStyle: CardStyle): CardStyleSpec {
  const [r, g, b] = hexToRgb(hex);
  const rgb = `${r}, ${g}, ${b}`;

  switch (cardStyle) {
    // ─── Estilos legados (retrocompat) ─────────────────────────
    case "colorful":
    case "light":
    case "assorted":
      return {
        style: {
          background: `rgba(${rgb}, 0.13)`,
          borderLeft: `3px solid ${hex}`,
          color: "hsl(var(--card-text-soft))",
        },
      };

    // ─── Novos estilos profissionais ───────────────────────────

    // "Bordas marcadas" — borda completa em toda a volta, bg suave.
    // Dá a sensação de cada consulta ser um "quadro" bem delimitado.
    case "bordered":
      return {
        style: {
          background: `rgba(${rgb}, 0.08)`,
          border: `1.5px solid ${hex}`,
          color: "hsl(var(--card-text-soft))",
        },
      };

    // Fundo saturado + texto branco/escuro conforme luminância.
    // Máxima visibilidade — bom pra quem olha a agenda de longe.
    case "solid":
      return {
        style: {
          background: hex,
          border: `1px solid rgba(0, 0, 0, 0.08)`,
          // Suavizado: zinc-900 em vez de preto puro, zinc-50 em vez de branco puro
          color: isLight(hex) ? "#27272a" : "#fafafa",
        },
        textClassName: "font-semibold",
      };

    // Bg branco + barra lateral GROSSA colorida (6px).
    // Muito clean, estilo Linear/Apple Calendar.
    case "minimal":
      return {
        style: {
          background: "hsl(var(--card))",
          borderLeft: `6px solid ${hex}`,
          boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
          color: "hsl(var(--card-text-soft))",
        },
      };

    // Bg branco + borda fina completa + ponto colorido no topo esquerdo.
    // Elegante e clínico, evoca prontuário médico.
    case "outlined":
      return {
        style: {
          background: "hsl(var(--card))",
          border: `1px solid rgba(${rgb}, 0.5)`,
          boxShadow: `inset 4px 0 0 -1px ${hex}`,
          color: "hsl(var(--card-text-soft))",
        },
      };

    // Sticker — bg saturado + shadow colorida forte (efeito elevação).
    // Destaca cada appointment como um "post-it" individual.
    case "sticker":
      return {
        style: {
          background: `rgba(${rgb}, 0.22)`,
          border: `1px solid rgba(${rgb}, 0.4)`,
          boxShadow: `0 2px 8px -2px rgba(${rgb}, 0.5), 0 1px 2px 0 rgba(${rgb}, 0.3)`,
          color: "hsl(var(--card-text-soft))",
        },
      };

    // ─── Focados em DESTACAR A BORDA ───────────────────────────
    // Separação visual nítida entre blocos adjacentes (lado a lado) sem
    // mudar o preenchimento, só engrossando/reforçando o contorno.

    // Borda fina — 1px sólido + ring externo sutil na mesma cor.
    // Separa blocos vizinhos com uma linha clara sem "peso visual".
    case "bordered-thin":
      return {
        style: {
          background: `rgba(${rgb}, 0.1)`,
          border: `1px solid ${hex}`,
          boxShadow: `0 0 0 1px rgba(${rgb}, 0.15)`,
          color: "hsl(var(--card-text-soft))",
        },
      };

    // Borda forte — 2.5px + ring branco interno + sombra colorida.
    // Máxima separação: identifica blocos distintos mesmo quando têm
    // o mesmo procedimento (mesma cor) e estão grudados.
    case "bordered-strong":
      return {
        style: {
          background: `rgba(${rgb}, 0.13)`,
          border: `2.5px solid ${hex}`,
          boxShadow: `inset 0 0 0 1px rgba(255, 255, 255, 0.55), 0 2px 6px -2px rgba(${rgb}, 0.55)`,
          color: "hsl(var(--card-text-soft))",
        },
        textClassName: "font-semibold",
      };

    // ─── Modernos / minimalistas SOTA ──────────────────────────

    // Glass — glassmorphism premium (vibe iOS/Vercel 2026). Translúcido
    // com backdrop-blur, highlight interno simulando "vidro fosco", borda
    // bem fina colorida. Fica lindo sobre qualquer bg (branco, grid).
    case "glass":
      return {
        style: {
          background: `rgba(${rgb}, 0.14)`,
          border: `1px solid rgba(${rgb}, 0.3)`,
          backdropFilter: "blur(10px) saturate(160%)",
          WebkitBackdropFilter: "blur(10px) saturate(160%)",
          boxShadow: `inset 0 1px 0 0 rgba(255, 255, 255, 0.4), 0 1px 3px 0 rgba(${rgb}, 0.18), 0 0 0 1px rgba(255, 255, 255, 0.1) inset`,
          color: "hsl(var(--card-text-soft))",
        },
      };

    // Ribbon — fita colorida NO TOPO (não na lateral). Bg branco limpo,
    // leitura maximizada (texto escuro sobre branco), cor usada como
    // acento superior. Visual Notion/Linear 2026.
    case "ribbon":
      return {
        style: {
          background: "hsl(var(--card))",
          border: `1px solid rgba(${rgb}, 0.25)`,
          borderTop: `3px solid ${hex}`,
          boxShadow: `0 1px 2px 0 rgba(0, 0, 0, 0.04), 0 1px 6px -2px rgba(${rgb}, 0.22)`,
          color: "hsl(var(--card-text-soft))",
        },
      };

    default:
      // Exhaustive check — se alguém adiciona um valor novo no enum sem aqui, TS reclama.
      return {
        style: {
          background: `rgba(${rgb}, 0.13)`,
          borderLeft: `3px solid ${hex}`,
          color: "hsl(var(--card-text-soft))",
        },
      };
  }
}
